import React from "react";
import styled from "styled-components";
import { useDrag, useDrop } from "react-dnd";
import { Identifier, XYCoord } from "dnd-core";

import { Accordion, AccordionDetails, AccordionSummary, Backdrop } from "@mui/material";

import Button from "@ingka/button";
import ListBox, { MenuItem } from "@ingka/list-box";
import { colourNeutral3 } from "@ingka/variables/colours-css";
import dragHandle from "@ingka/ssr-icon/paths/circle-grid-2x3-filled";
import arrowUp from "@ingka/ssr-icon/paths/arrow-up";
import arrowDown from "@ingka/ssr-icon/paths/arrow-down";
import moveTop from "@ingka/ssr-icon/paths/chevron-double-up";
import moveBottom from "@ingka/ssr-icon/paths/chevron-double-down";
import SSRIcon from "@ingka/ssr-icon";
import chevronDown from "@ingka/ssr-icon/paths/chevron-down";
import ellipsesVertical from "@ingka/ssr-icon/paths/ellipses-vertical";
import Tooltip from "@ingka/tooltip";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import copy from "@ingka/ssr-icon/paths/copy";
import documentAdd from "@ingka/ssr-icon/paths/document";
import LayoutHeading from "../../LayoutHeading";

interface DragItem {
	index: number;
	id: string;
	type: string;
}

export const ItemTypes = {
	CARD: "card",
	CHILD: "child",
};

interface DispatchPropsOfCardRenderer {
	onRemove(): () => void;
	onMoveUp(): () => void;
	onMoveDown(): () => void;
	onMoveToTop(): () => void;
	onMoveToBottom(): () => void;
}

interface CardRendererLayoutProps extends DispatchPropsOfCardRenderer {
	index: number;
	data: number;
	moveCard: (from: number, to: number) => void;
	dragFocusIndex?: number;
	isDraggingSelected: boolean;
	showMenu: boolean;
	setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
	panelLabel: string;
	panelAriaLabel: string;
	handleOnKeyPressed: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
	handleKeyUp: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
	itemsToRender: React.ReactNode;
	enabled: boolean;
	path: string;
	onCopyArrayValues: (index: number) => void;
	onPasteArrayValues: (index: number) => void;
	canPaste: boolean;
	label?: string;
	removable?: boolean;
	isFocused?: boolean;
	headingLevel?: number;
	showAccrordion?: boolean;
	isChildArray?: boolean;
	noBorder?: boolean;
}

const ItemsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding-bottom: 0.75rem;
	padding-top: 0.75rem;
`;

const StyledButton = styled(Button)`
	margin-right: 0.75rem;
`;

const StyledDragButton = styled(Button)<{ $noBorder?: boolean }>`
	cursor: move;
	margin-right: 0.75rem;
	${({ $noBorder }) =>
		$noBorder &&
		`
	    margin-bottom: 1.25rem;
	`}
	.btn:hover {
		color: transparent;
	}
`;

const ItemsRendererWrapper = styled.div<{ $showAccrordion?: boolean; $noBorder?: boolean }>`
	${({ $showAccrordion }) =>
		$showAccrordion
			? `display: flex;
	align-items: center;
	gap: 0.5rem;
	flex: 1;
	padding-left: 0.1rem;
	`
			: `
	border: 1px solid ${colourNeutral3};
	border-radius: 4px;
	padding: 0.75rem;
	width: 100%;`};

	${({ $noBorder }) =>
		$noBorder &&
		`
	border: none;
	padding: 0;
	`}

	.MuiPaper-root {
		box-shadow: none;
		width: 100%;
	}
`;

const StyledListBox = styled(ListBox)`
	z-index: 1000;
	.menu-item__selected-icon {
		display: none;
	}
`;

const OptionsWrapper = styled.div<{ $noBorder?: boolean }>`
	display: flex;
	justify-content: center;
	margin-left: 0.75rem;
	${({ $noBorder }) =>
		$noBorder &&
		`
	    margin-bottom: 1.25rem;
	`}
	gap: 0.5rem;
`;

const StyledAccordion = styled(Accordion)<{ $isDragging: boolean }>`
	border: 1px solid ${colourNeutral3};
	opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
	&.MuiAccordion-root::before {
		content: none;
	}
	.MuiAccordionDetails-root {
		display: block;
	}
	.MuiAccordionSummary-root {
		.MuiAccordionSummary-expandIcon.Mui-expanded {
			transform: none;
			.chevron-down {
				transform: rotate(180deg);
			}

			.MuiTouchRipple-root {
				display: none;
			}
		}
	}
`;

const CardRendererLayout: React.FC<CardRendererLayoutProps> = ({
	isFocused,
	index,
	dragFocusIndex,
	data,
	moveCard,
	onMoveDown,
	onMoveUp,
	onMoveToBottom,
	onMoveToTop,
	onRemove,
	showMenu,
	panelLabel,
	panelAriaLabel,
	isDraggingSelected,
	setShowMenu,
	handleOnKeyPressed,
	handleKeyUp,
	headingLevel,
	itemsToRender,
	enabled,
	path,
	onCopyArrayValues,
	onPasteArrayValues,
	canPaste,
	removable = true,
	showAccrordion = false,
	isChildArray = false,
	noBorder = false,
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const dndRef = React.useRef<HTMLDivElement>(null);
	const focusRef = React.useRef<HTMLDivElement>(null);
	const menuRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (isFocused && ref.current && showAccrordion) {
			ref.current.focus();
		} else if (isFocused) {
			// eslint-disable-next-line quotes
			const FOCUSABLE_SELECTORS = 'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';
			const firstEl = ref.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
			firstEl?.focus();
		}
	}, [isFocused, showAccrordion]);

	React.useEffect(() => {
		if (dragFocusIndex === index && focusRef.current) {
			const button = focusRef.current?.querySelector("button");
			button?.focus();
		}
	}, [dragFocusIndex, index]);

	const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
		accept: isChildArray ? ItemTypes.CHILD : ItemTypes.CARD,
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: DragItem, monitor) {
			if (!dndRef.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;

			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = dndRef.current?.getBoundingClientRect();

			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			moveCard(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag, preview] = useDrag({
		type: isChildArray ? ItemTypes.CHILD : ItemTypes.CARD,
		item: () => {
			return { index };
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const handleMenuBlur = React.useCallback(
		(e: React.FocusEvent<HTMLDivElement>) => {
			if (menuRef.current && menuRef.current.contains(e.relatedTarget as Node)) {
				return;
			}
			setShowMenu(false);
		},
		[setShowMenu]
	);

	drop(dndRef);

	const [ready, setReady] = React.useState(false);

	return (
		<ItemsWrapper role="listitem" ref={dndRef}>
			<Backdrop sx={{ "z-index": 1000 }} open={showMenu} onClick={() => setShowMenu(false)} invisible />
			<div ref={focusRef} role="application">
				<StyledDragButton
					$noBorder={noBorder}
					iconOnly
					id={`${panelLabel}-drag-handle`}
					ssrIcon={dragHandle}
					aria-describedby="drag-instructions"
					aria-label={`Drag to reorder ${panelLabel}, ${index + 1} of ${data}`}
					text={`Drag to reorder ${panelLabel}`}
					aria-pressed={isDraggingSelected}
					size="small"
					type="tertiary"
					ref={drag}
					onKeyDown={handleOnKeyPressed}
					onKeyUp={handleKeyUp}
				/>
			</div>
			<ItemsRendererWrapper $showAccrordion={showAccrordion} $noBorder={noBorder}>
				{showAccrordion ? (
					<StyledAccordion
						ref={preview as React.Ref<HTMLDivElement> | undefined}
						data-handler-id={handlerId}
						$isDragging={isDragging}
						TransitionProps={{
							unmountOnExit: true,
							onEntered: () => setReady(true),
							onExit: () => setReady(false),
						}}
					>
						<AccordionSummary
							expandIcon={<SSRIcon className="chevron-down" paths={chevronDown} />}
							aria-controls={`panel-${index}-content`}
							id={`panel-${index}-header`}
							ref={ref}
							aria-label={panelAriaLabel}
						>
							<LayoutHeading label={panelLabel} headingLevel={headingLevel} />
						</AccordionSummary>

						<AccordionDetails id={`panel-${index}-content`}>
							<div aria-hidden={!ready}>{itemsToRender}</div>
						</AccordionDetails>
					</StyledAccordion>
				) : (
					<div ref={preview as React.Ref<HTMLDivElement> | undefined}>
						<div ref={ref}>{itemsToRender}</div>
					</div>
				)}
			</ItemsRendererWrapper>
			<OptionsWrapper $noBorder={noBorder}>
				<div ref={menuRef} onBlur={handleMenuBlur}>
					<StyledListBox
						position="left"
						open={showMenu}
						role="menu"
						domFocus
						ariaLabel="Actions"
						trigger={
							<Button
								onClick={() => {
									setShowMenu(!showMenu);
								}}
								size="small"
								iconOnly
								text="More options"
								type="tertiary"
								aria-haspopup="menu"
								aria-expanded={showMenu}
								ssrIcon={ellipsesVertical}
							/>
						}
					>
						<MenuItem
							id="move-to-top"
							leadingIcon={moveTop}
							title="Move to top"
							onClick={() => {
								onMoveToTop();
								setShowMenu(false);
							}}
							aria-label={`Move to top ${panelLabel}`}
							disabled={index === 0 || !enabled}
						/>
						<MenuItem
							id="move-up"
							leadingIcon={arrowUp}
							onClick={() => {
								onMoveUp();
								setShowMenu(false);
							}}
							title="Move up"
							disabled={index === 0 || !enabled}
							aria-label={`Move up ${panelLabel}`}
						/>
						<MenuItem
							id="move-down"
							leadingIcon={arrowDown}
							onClick={() => {
								onMoveDown();
								setShowMenu(false);
							}}
							title="Move down"
							disabled={index === data - 1 || !enabled}
							aria-label={`Move down ${panelLabel}`}
						/>
						<MenuItem
							id="move-to-bottom"
							leadingIcon={moveBottom}
							onClick={() => {
								onMoveToBottom();
								setShowMenu(false);
							}}
							title="Move to bottom"
							disabled={index === data - 1 || !enabled}
							aria-label={`Move to bottom ${panelLabel}`}
						/>
						<MenuItem
							id="copy-array-values"
							leadingIcon={copy}
							onClick={() => {
								onCopyArrayValues(index);
								setShowMenu(false);
							}}
							title="Copy array values"
							disabled={!enabled}
							aria-label={`Copy array values from ${panelLabel}`}

						/>
						<MenuItem
							id="paste-array-values"
							leadingIcon={documentAdd}
							onClick={() => {
								onPasteArrayValues(index);
								setShowMenu(false);
							}}
							title="Paste array values"
							disabled={!enabled || !canPaste}
							aria-label={"Paste array values"}						/>
					</StyledListBox>
				</div>
				<Tooltip interactiveCustomTrigger tooltipText="Remove">
					<StyledButton
						id={`remove-field-${path}`}
						type="tertiary"
						onClick={onRemove}
						text={`Remove ${panelLabel}`}
						iconOnly
						size="small"
						ssrIcon={trashCan}
						disabled={!enabled || removable === false}
					/>
				</Tooltip>
			</OptionsWrapper>
		</ItemsWrapper>
	);
};

export default CardRendererLayout;
